import { useState, useEffect } from 'react'
import api from '../lib/api'
import { getUserTimezone } from '../lib/timezone'
import { useToast } from '../hooks/use-toast'
import { BarChart3, MessageSquare, Users, Target, Clock, Phone, PhoneIncoming, AlertCircle, Download } from 'lucide-react'
import { DateRange } from 'react-day-picker'

// Components
import { PageHeader } from '@/components/dashboard-ui'
import TimeRangeSelector from '../components/analytics/TimeRangeSelector'
import AgentPerformanceTable from '../components/analytics/AgentPerformanceTable'
import ChatHeatmap from '../components/analytics/ChatHeatmap'
import ConversionFunnel from '../components/analytics/ConversionFunnel'
import UtmAnalytics from '../components/UtmAnalytics'
import CommonQuestions from '../components/analytics/CommonQuestions'
import TopPages from '../components/analytics/TopPages'
import FlaggedMessages from '../components/analytics/FlaggedMessages'
import CallVolumeChart from '../components/analytics/CallVolumeChart'
import CallOutcomeBreakdown from '../components/analytics/CallOutcomeBreakdown'
import CallbackSummaryCard from '../components/analytics/CallbackSummaryCard'
import SentimentSummary from '../components/analytics/SentimentSummary'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface Analytics {
  overview: {
    totalConversations: number
    completedConversations: number
    activeConversations: number
    totalMessages: number
    avgMessagesPerConversation: string
    completionRate: string
  }
  chartData: Array<{
    date: string
    count: number
  }>
  timeframe: string
}

interface AgentPerformance {
  id: string
  name: string
  email: string
  avatar?: string
  status: 'available' | 'busy' | 'offline'
  totalConversations: number
  totalMessages: number
  avgResponseTime: number
  satisfactionScore?: number
}

interface HeatmapData {
  day: number
  hour: number
  count: number
}

interface FunnelStage {
  name: string
  count: number
  color: string
}

interface PageData {
  path: string
  fullUrl?: string
  title: string
  pageType?: string
  chatCount: number
  uniqueVisitors: number
  bookingClicks?: number
  bookingCompletions?: number
  avgMessages?: string
  clickConversionRate?: number
  bookingConversionRate?: number
}

interface VoiceAnalytics {
  overview: {
    totalCalls: number
    inbound: number
    outbound: number
    completed: number
    missed: number
    voicemail: number
    failed: number
    answerRate: number
    totalMinutes: number
    avgDurationSeconds: number
    totalCost: number
    callbacksNeeded: number
    callbacksCompleted: number
    avgQualificationScore: number
  }
  dailyVolume: Array<{ date: string; inbound: number; outbound: number; missed: number }>
  heatmap: { data: number[][]; dayLabels: string[]; maxValue: number }
  outcomeBreakdown: { completed: number; missed: number; voicemail: number; failed: number; cancelled: number }
  sentimentBreakdown: { positive: number; neutral: number; negative: number; unrated: number }
  callbackSummary: { needed: number; queued: number; completed: number; resolved: number; dismissed: number }
  caseTypes?: Array<{ caseType: string; count: number; avgScore: number }>
  directionBreakdown?: Record<string, { total: number; completed: number; voicemail: number; missed: number; avgDuration: number; bookings: number }>
}

export default function AnalyticsPage() {
  const { toast } = useToast()
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>([])
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([])
  const [funnelData, setFunnelData] = useState<FunnelStage[]>([])
  const [topPages, setTopPages] = useState<PageData[]>([])
  const [voiceAnalytics, setVoiceAnalytics] = useState<VoiceAnalytics | null>(null)
  const [voiceHeatmapData, setVoiceHeatmapData] = useState<HeatmapData[]>([])
  const [voiceLoading, setVoiceLoading] = useState(true)
  const [heatmapLoading, setHeatmapLoading] = useState(true)
  const [timeframe, setTimeframe] = useState('7d')
  const [customRange, setCustomRange] = useState<DateRange | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadAnalytics()
    loadAgentPerformance()
    loadHeatmapData()
    loadFunnelData()
    loadTopPages()
    loadVoiceAnalytics()
  }, [timeframe, customRange])

  const userTz = encodeURIComponent(getUserTimezone())

  const loadAnalytics = async () => {
    try {
      const response = await api.get(`/api/analytics/overview?timeframe=${timeframe}&timezone=${userTz}`)
      if (response.data.success) {
        setAnalytics(response.data.analytics)
      }
    } catch (error) {
      toast({ title: 'Failed to load analytics', description: 'Some data may be unavailable. Try refreshing.', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadAgentPerformance = async () => {
    try {
      const response = await api.get(`/api/analytics/agents?timeframe=${timeframe}&timezone=${userTz}`)
      if (response.data.success) {
        setAgentPerformance(response.data.agentPerformance)
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load analytics data', variant: 'destructive' })
    }
  }

  const loadHeatmapData = async () => {
    setHeatmapLoading(true)
    try {
      const tz = getUserTimezone()
      const response = await api.get(`/api/analytics/heatmap?timeframe=${timeframe}&timezone=${encodeURIComponent(tz)}`)
      if (response.data.success && response.data.heatmap) {
        // Transform 7x24 matrix to HeatmapData[] array
        const matrix = response.data.heatmap.data
        const heatmapArray: HeatmapData[] = []
        for (let day = 0; day < 7; day++) {
          for (let hour = 0; hour < 24; hour++) {
            heatmapArray.push({
              day,
              hour,
              count: matrix[day]?.[hour] || 0
            })
          }
        }
        setHeatmapData(heatmapArray)
      }
    } catch (error) {
      // Fall back to empty data on error
      setHeatmapData([])
    } finally {
      setHeatmapLoading(false)
    }
  }

  const loadFunnelData = async () => {
    try {
      const response = await api.get(`/api/analytics/funnel?timeframe=${timeframe}&timezone=${userTz}`)
      if (response.data.success) {
        setFunnelData(response.data.funnelData)
      }
    } catch (error) {
      // silently ignore — non-critical
    }
  }

  const loadTopPages = async () => {
    try {
      const response = await api.get(`/api/analytics/top-pages?timeframe=${timeframe}&limit=10&timezone=${userTz}`)
      if (response.data.success) {
        setTopPages(response.data.data)
      }
    } catch (error) {
      // silently ignore — non-critical
    }
  }

  const loadVoiceAnalytics = async () => {
    setVoiceLoading(true)
    try {
      const voiceTz = getUserTimezone()
      const response = await api.get(`/api/analytics/voice?timeframe=${timeframe}&timezone=${encodeURIComponent(voiceTz)}`)
      if (response.data.success && response.data.voiceAnalytics) {
        setVoiceAnalytics(response.data.voiceAnalytics)
        // Transform heatmap matrix to HeatmapData[]
        const matrix = response.data.voiceAnalytics.heatmap.data
        const heatmapArray: HeatmapData[] = []
        for (let day = 0; day < 7; day++) {
          for (let hour = 0; hour < 24; hour++) {
            heatmapArray.push({
              day,
              hour,
              count: matrix[day]?.[hour] || 0
            })
          }
        }
        setVoiceHeatmapData(heatmapArray)
      }
    } catch (error) {
      setVoiceAnalytics(null)
      setVoiceHeatmapData([])
    } finally {
      setVoiceLoading(false)
    }
  }

  const formatDuration = (seconds: number) => {
    if (!seconds) return 'N/A'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  }

  const handleTimeRangeChange = (value: string, customRange?: DateRange) => {
    setTimeframe(value)
    if (customRange) {
      setCustomRange(customRange)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-10 w-full sm:w-96 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  const handleExportCSV = () => {
    const rows: string[][] = []

    // Chat overview
    if (analytics?.overview) {
      rows.push(['--- Chat Overview ---'])
      rows.push(['Metric', 'Value'])
      rows.push(['Total Conversations', String(analytics.overview.totalConversations)])
      rows.push(['Completed Conversations', String(analytics.overview.completedConversations)])
      rows.push(['Completion Rate', analytics.overview.completionRate + '%'])
      rows.push(['Total Messages', String(analytics.overview.totalMessages)])
      rows.push(['Avg Messages/Conversation', analytics.overview.avgMessagesPerConversation])
      rows.push([])
    }

    // Agent performance
    if (agentPerformance.length > 0) {
      rows.push(['--- Agent Performance ---'])
      rows.push(['Name', 'Email', 'Conversations', 'Messages', 'Avg Response Time (s)'])
      agentPerformance.forEach(a => {
        rows.push([a.name, a.email, String(a.totalConversations), String(a.totalMessages), String(a.avgResponseTime)])
      })
      rows.push([])
    }

    // Voice overview
    if (voiceAnalytics?.overview) {
      const v = voiceAnalytics.overview
      rows.push(['--- Call Overview ---'])
      rows.push(['Metric', 'Value'])
      rows.push(['Total Calls', String(v.totalCalls)])
      rows.push(['Inbound', String(v.inbound)])
      rows.push(['Outbound', String(v.outbound)])
      rows.push(['Completed', String(v.completed)])
      rows.push(['Missed', String(v.missed)])
      rows.push(['Voicemail', String(v.voicemail)])
      rows.push(['Answer Rate', v.answerRate + '%'])
      rows.push(['Avg Duration (s)', String(v.avgDurationSeconds)])
      rows.push(['Total Minutes', String(v.totalMinutes)])
      rows.push([])
    }

    // Daily call volume
    if (voiceAnalytics?.dailyVolume?.length) {
      rows.push(['--- Daily Call Volume ---'])
      rows.push(['Date', 'Inbound', 'Outbound', 'Missed'])
      voiceAnalytics.dailyVolume.forEach(d => {
        rows.push([d.date, String(d.inbound), String(d.outbound), String(d.missed)])
      })
      rows.push([])
    }

    // Funnel data
    if (funnelData.length > 0) {
      rows.push(['--- Conversion Funnel ---'])
      rows.push(['Stage', 'Count'])
      funnelData.forEach(s => {
        rows.push([s.name, String(s.count)])
      })
      rows.push([])
    }

    // Top pages
    if (topPages.length > 0) {
      rows.push(['--- Top Pages ---'])
      rows.push(['Page', 'Chats', 'Unique Visitors'])
      topPages.forEach(p => {
        rows.push([p.path, String(p.chatCount || 0), String(p.uniqueVisitors || 0)])
      })
    }

    const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${timeframe}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0 overflow-x-clip">
      <PageHeader
        eyebrow="Insights"
        title="Analytics"
        subtitle="Track performance, identify trends, and optimize operations."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-brand-royal" />
          </div>
        }
        actions={analytics ? (
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        ) : undefined}
      />

      <div className="min-w-0">
        <TimeRangeSelector
          value={timeframe}
          onChange={handleTimeRangeChange}
        />
      </div>

      <Tabs defaultValue="chat" className="space-y-6">
        <TabsList>
          <TabsTrigger value="chat">
            <MessageSquare className="h-4 w-4 mr-2" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="calls">
            <Phone className="h-4 w-4 mr-2" />
            Calls
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="space-y-6">
          {!analytics ? (
            <Card className="card-inset">
              <CardContent className="py-16">
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="h-16 w-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-4">
                    <BarChart3 className="w-8 h-8 text-primary/40" />
                  </div>
                  <p className="font-semibold text-foreground/80">No analytics data yet</p>
                  <p className="text-muted-foreground text-sm mt-1 max-w-sm">
                    Data appears as visitors interact with your chat widget and AI voice agents. Install the widget or assign a phone number to get started.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overview Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="card-inset">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.overview.totalConversations}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.overview.activeConversations} currently active
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-inset">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                    <Target className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.overview.completionRate}%</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.overview.completedConversations} completed
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-inset">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.overview.totalMessages}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.overview.avgMessagesPerConversation} avg per chat
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-inset">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Human Takeovers</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {agentPerformance.filter(a => a.totalConversations > 0).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {agentPerformance.filter(a => a.totalConversations > 0).length === 0
                        ? 'AI handling all chats'
                        : `${agentPerformance.filter(a => a.totalConversations > 0).length} staff stepped in`}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Heatmap and Funnel Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChatHeatmap data={heatmapData} loading={heatmapLoading} />
                <ConversionFunnel data={funnelData} loading={false} />
              </div>

              {/* UTM and Top Pages Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UtmAnalytics timeframe={timeframe} />
                <TopPages data={topPages} loading={false} />
              </div>

              {/* Common Questions Analysis */}
              <CommonQuestions timeframe={timeframe} />

              {/* Flagged Messages */}
              <FlaggedMessages timeframe={timeframe} />
            </>
          )}

          {/* Agent Performance Leaderboard */}
          <AgentPerformanceTable data={agentPerformance} loading={false} />
        </TabsContent>

        <TabsContent value="calls" className="space-y-6">
          {/* Stat Cards — 6 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{voiceAnalytics?.overview.totalCalls ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {voiceAnalytics?.overview.totalMinutes ?? 0} total minutes
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Inbound</CardTitle>
                <PhoneIncoming className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{voiceAnalytics?.overview.inbound ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {voiceAnalytics?.overview.answerRate ?? 0}% answer rate
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Outbound</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{voiceAnalytics?.overview.outbound ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {voiceAnalytics?.overview.voicemail ?? 0} voicemails left
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatDuration(voiceAnalytics?.overview.avgDurationSeconds ?? 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {voiceAnalytics?.overview.completed ?? 0} completed
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Lead Quality</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {voiceAnalytics?.overview.avgQualificationScore ? (
                  <>
                    <div className="text-2xl font-bold">{voiceAnalytics.overview.avgQualificationScore}/10</div>
                    <p className="text-xs text-muted-foreground mt-1">Avg qualification score</p>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-medium text-muted-foreground">Not enough data</div>
                    <p className="text-xs text-muted-foreground mt-1">Scores are generated from completed call transcripts via AI analysis</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Follow-ups</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{voiceAnalytics?.overview.callbacksNeeded ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {voiceAnalytics?.overview.callbacksCompleted ?? 0} completed
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Heatmap and Outcome Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChatHeatmap
              data={voiceHeatmapData}
              loading={voiceLoading}
              title="Call Volume Heatmap"
              description="Peak call times by day and hour"
              tooltipLabel="call"
            />
            <CallOutcomeBreakdown
              data={voiceAnalytics?.outcomeBreakdown ?? { completed: 0, missed: 0, voicemail: 0, failed: 0, cancelled: 0 }}
              loading={voiceLoading}
            />
          </div>

          {/* Daily Volume and Sentiment */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CallVolumeChart
              data={voiceAnalytics?.dailyVolume ?? []}
              loading={voiceLoading}
            />
            <SentimentSummary
              data={voiceAnalytics?.sentimentBreakdown ?? { positive: 0, neutral: 0, negative: 0, unrated: 0 }}
              avgQualificationScore={voiceAnalytics?.overview.avgQualificationScore}
              loading={voiceLoading}
            />
          </div>

          {/* Inbound vs Outbound Breakdown */}
          {voiceAnalytics?.directionBreakdown && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {['inbound', 'outbound'].map(dir => {
                const d = voiceAnalytics.directionBreakdown?.[dir]
                if (!d || d.total === 0) return null
                return (
                  <Card key={dir}>
                    <CardHeader>
                      <CardTitle className="text-base capitalize flex items-center gap-2">
                        {dir === 'inbound' ? <PhoneIncoming className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                        {dir} Calls
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-2xl font-bold">{d.total}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{d.completed}</p>
                          <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{d.voicemail}</p>
                          <p className="text-xs text-muted-foreground">Voicemail</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{d.missed}</p>
                          <p className="text-xs text-muted-foreground">Missed</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{formatDuration(d.avgDuration)}</p>
                          <p className="text-xs text-muted-foreground">Avg Duration</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{d.bookings}</p>
                          <p className="text-xs text-muted-foreground">Bookings</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}

          {/* Top Call Reasons / Case Types */}
          {voiceAnalytics?.caseTypes && voiceAnalytics.caseTypes.length > 0 && (
            <Card className="card-inset">
              <CardHeader>
                <CardTitle className="text-base">Top Call Reasons</CardTitle>
                <CardDescription>Most common case types from AI call analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {voiceAnalytics.caseTypes.map((ct) => {
                    const maxCount = voiceAnalytics.caseTypes![0].count
                    const pct = maxCount > 0 ? (ct.count / maxCount) * 100 : 0
                    return (
                      <div key={ct.caseType} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{ct.caseType}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold">{ct.count}</span>
                            {ct.avgScore > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {ct.avgScore}/10 quality
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary rounded-full transition-colors"
                            style={{ width: `${Math.max(pct, 3)}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Callback Summary */}
          <CallbackSummaryCard
            data={voiceAnalytics?.callbackSummary ?? { needed: 0, queued: 0, completed: 0, resolved: 0, dismissed: 0 }}
            loading={voiceLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
